import { SITE_URL } from "@/components/Seo";
import { SERVICES, BUSINESS } from "@/data/services";

/**
 * Central JSON-LD builder — reusable across pages.
 * Keeps schema.org markup consistent and Google-friendly.
 */

export const ORG_ID = `${SITE_URL}/#organization`;
export const LOCAL_BUSINESS_ID = `${SITE_URL}/#localbusiness`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Darkside Detailing",
    alternateName: "Darksid3 Detailing",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/darkside-logo-horizontal.png`,
      width: 512,
      height: 128,
    },
    sameAs: [BUSINESS.facebook, BUSINESS.instagram],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-782-321-9302",
      contactType: "customer service",
      email: BUSINESS.email,
      areaServed: "CA-NS",
      availableLanguage: "en",
    },
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["AutoDetailing", "LocalBusiness"],
    "@id": LOCAL_BUSINESS_ID,
    name: "Darkside Detailing",
    image: `${SITE_URL}/darkside-logo-horizontal.png`,
    url: SITE_URL,
    telephone: "+1-782-321-9302",
    email: "bookings@darksidedetailing.ca",
    priceRange: "$$",
    description:
      "Premium mobile auto detailing serving Halifax Regional Municipality. Fully licensed & insured. We bring the water, power, and wet-look finish to your driveway.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Dartmouth",
      addressRegion: "NS",
      addressCountry: "CA",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 44.6717,
      longitude: -63.5772,
    },
    areaServed: [
      { "@type": "City", name: "Halifax" },
      { "@type": "City", name: "Dartmouth" },
      { "@type": "City", name: "Bedford" },
      { "@type": "City", name: "Sackville" },
      { "@type": "City", name: "Cole Harbour" },
      { "@type": "City", name: "Clayton Park" },
      { "@type": "City", name: "Spryfield" },
      { "@type": "City", name: "Eastern Passage" },
      { "@type": "AdministrativeArea", name: "Halifax Regional Municipality" },
    ],
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 44.6717,
        longitude: -63.5772,
      },
      geoRadius: 40000,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
        opens: "09:00",
        closes: "18:00",
      },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "09:00", closes: "14:00" },
    ],
    paymentAccepted: "Debit, Credit Card, E-Transfer, Cash",
    currenciesAccepted: "CAD",
    foundingDate: "2021",
    sameAs: [BUSINESS.facebook, BUSINESS.instagram],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "100",
      bestRating: "5",
      worstRating: "1",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Mobile Auto Detailing Services",
      itemListElement: SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: s.name,
          description: s.description,
        },
        price: s.pricing.sedan,
        priceCurrency: "CAD",
      })),
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL,
    name: "Darkside Detailing",
    publisher: { "@id": ORG_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function serviceSchema(service, slug) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/services/${slug}#service`,
    name: service.name,
    description: service.description,
    provider: { "@id": LOCAL_BUSINESS_ID },
    serviceType: "Auto Detailing",
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Halifax Regional Municipality",
    },
    audience: {
      "@type": "Audience",
      audienceType: "Vehicle Owners in Nova Scotia",
    },
    offers: [
      {
        "@type": "Offer",
        name: `${service.name} — Sedan`,
        price: service.pricing.sedan,
        priceCurrency: "CAD",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/services/${slug}`,
      },
      {
        "@type": "Offer",
        name: `${service.name} — SUV / Half-Ton`,
        price: service.pricing.suv,
        priceCurrency: "CAD",
        availability: "https://schema.org/InStock",
      },
      {
        "@type": "Offer",
        name: `${service.name} — Larger SUV / Van / 2500+ Truck`,
        price: service.pricing.large,
        priceCurrency: "CAD",
        availability: "https://schema.org/InStock",
      },
    ],
    // Note: aggregateRating intentionally omitted here — Google requires it to be
    // attached to Product/LocalBusiness/Organization or paired with individual Review
    // items to be valid. Our LocalBusiness schema (emitted on the same page) already
    // carries the site-wide 4.9 rating.
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function serviceAreaItemListSchema(locations, siteUrl = SITE_URL) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${siteUrl}/#service-areas`,
    name: "Mobile Detailing Service Areas — Halifax Regional Municipality",
    itemListElement: locations.map((loc, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${siteUrl}/service-areas/${loc.slug}`,
      name: `Mobile Detailing in ${loc.name}`,
    })),
  };
}

export function articleSchema({ title, description, path, image, published, modified, author = "Darkside Detailing" }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE_URL}${path}#article`,
    headline: title,
    description,
    image: image ? `${SITE_URL}${image}` : `${SITE_URL}/darkside-logo-horizontal.png`,
    datePublished: published,
    dateModified: modified || published,
    author: { "@type": "Organization", name: author, url: SITE_URL },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: `${SITE_URL}${path}`,
  };
}
